import { API_BASE_URL, ApiError } from './client';
import { ValidationFailure } from './events';
import type { FieldErrors } from './events';
import type { AnnouncementAttachment, AttachmentInput, UploadedDocument } from './announcements';
import { downsizeImage } from '../lib/imageDownsize';

/**
 * A unit's Info page — TROOP-256. Ordered sections a unit's officers write once: where it meets,
 * dues, the uniform, who to call.
 */

/** One section. Its attachments have an announcement's shape, so one component draws both. */
export interface InfoSection {
  id: string;
  title: string;
  /** Curated text: drawn by `CuratedText`, links with their host beside them. May be empty. */
  body: string;
  updatedAt: string;
  updatedByDisplayName: string;
  attachments: AnnouncementAttachment[];
}

export interface InfoPage {
  unitId: string;
  unitName: string;
  /**
   * The server's answer to "may this person change the page" — `UnitInfoAccessPolicy.CanEdit` —
   * so the screen never works the rule out for itself. Always false through `/family`.
   */
  canEdit: boolean;
  sections: InfoSection[];
}

export interface InfoSectionInput {
  title: string;
  body: string;
  /**
   * In display order. On a save, `{ id }` keeps one already there and a live one not listed is
   * detached (kept, never deleted); omitted or null leaves them as they are.
   */
  attachments?: (AttachmentInput | { id: string })[] | null;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string; errors?: FieldErrors } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    // 400 carries per-field messages, so the form can put each one under its own box.
    if (response.status === 400 && problem.errors) {
      throw new ValidationFailure(problem.errors);
    }

    throw new ApiError(response.status, problem.detail ?? problem.title ?? `Failed (${response.status}).`);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/** The guardian's door: every unit their children are in, each page named. Read only. */
export const familyInfoApi = {
  pages: () => call<{ pages: InfoPage[] }>('/family/info').then((r) => r.pages),
};

export const infoApi = {
  page: (unitId: string) => call<InfoPage>(`/units/${unitId}/info/`),

  addSection: (unitId: string, input: InfoSectionInput) =>
    call<{ sectionId: string }>(`/units/${unitId}/info/sections`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  saveSection: (unitId: string, sectionId: string, input: InfoSectionInput) =>
    call<void>(`/units/${unitId}/info/sections/${sectionId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  /** Takes it off the page. The server archives it — nothing is deleted. */
  removeSection: (unitId: string, sectionId: string) =>
    call<void>(`/units/${unitId}/info/sections/${sectionId}`, { method: 'DELETE' }),

  /** Every section on the page, in its new order. Refused if the page changed underneath. */
  reorder: (unitId: string, sectionIds: string[]) =>
    call<void>(`/units/${unitId}/info/order`, {
      method: 'PUT',
      body: JSON.stringify({ sectionIds }),
    }),

  /**
   * Uploads a document a section will hand out, before the section is saved — the same door shape
   * as an announcement's, under the page's own prefix. A picture is downsized first.
   */
  uploadDocument: async (unitId: string, file: File): Promise<UploadedDocument> => {
    const form = new FormData();

    if (file.type.startsWith('image/')) {
      const { blob } = await downsizeImage(file);
      form.append('file', blob, file.name);
    } else {
      form.append('file', file, file.name);
    }

    // No Content-Type header: the browser sets it with the multipart boundary.
    const response = await fetch(`${API_BASE_URL}/units/${unitId}/info/documents`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });

    if (!response.ok) {
      const problem = await response.json().catch(() => null);
      throw new ApiError(
        response.status,
        problem?.message ?? problem?.detail ?? problem?.title ?? 'That file could not be attached.',
      );
    }

    return (await response.json()) as UploadedDocument;
  },
};
